import { useEffect, useRef } from "react";
import {
  useAnnounce,
  useIsDialogOutletOnTop,
} from "../../Components/LiveAnnouncer/LiveAnnouncer";
import { describeStatusToasts } from "./toastPresentation";

export const STATUS_ANNOUNCE_GROUP_MS = 200;
export const STATUS_ANNOUNCE_MAX_WAIT_MS = 1000;

export default function useStatusToastAnnouncements(
  statusToasts,
  { shown, userData },
) {
  const announce = useAnnounce();
  const isDialogOutletOnTop = useIsDialogOutletOnTop();
  const seenRef = useRef(null);
  const queueRef = useRef([]);
  const timerRef = useRef(null);
  const capTimerRef = useRef(null);
  const latestRef = useRef({ statusToasts, shown, userData });

  const clearTimers = () => {
    clearTimeout(timerRef.current);
    clearTimeout(capTimerRef.current);
    timerRef.current = null;
    capTimerRef.current = null;
  };

  useEffect(() => {
    latestRef.current = { statusToasts, shown, userData };
  });

  useEffect(
    () => () => {
      clearTimers();
      queueRef.current = [];
    },
    [],
  );

  useEffect(() => {
    const seen = seenRef.current;
    seenRef.current = new Set(statusToasts.map((toast) => toast.id));
    if (!seen) return;
    const arrivals = statusToasts.filter((toast) => !seen.has(toast.id));
    if (!arrivals.length || !shown || !isDialogOutletOnTop()) return;
    const flush = () => {
      clearTimers();
      const queued = new Set(queueRef.current);
      queueRef.current = [];
      const latest = latestRef.current;
      if (!latest.shown || !isDialogOutletOnTop()) return;
      const message = describeStatusToasts(
        latest.statusToasts.filter((toast) => queued.has(toast.id)),
        latest.userData,
      );
      if (message) announce(message, { politeness: "polite" });
    };
    arrivals.forEach((toast) => queueRef.current.push(toast.id));
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, STATUS_ANNOUNCE_GROUP_MS);
    if (!capTimerRef.current) {
      capTimerRef.current = setTimeout(flush, STATUS_ANNOUNCE_MAX_WAIT_MS);
    }
  }, [statusToasts]);
}
